import React from 'react';
import { Box, Button, Link, Typography } from '@mui/material';
import { getInquiryCollectionLabel } from '../leadCollections';
import {
  getInquiryDisplayAt,
  getLeadItemLabelParts,
  getManualInquiryTags,
  isManualInquiry,
} from '../leadsConfig';
import InquiryOriginTags from './InquiryOriginTags';
import { leadsSummaryClearButtonSx } from './leadsUi.styles';
import { formatDateTimeShortAR } from '../../Utils/dateTimeFormat';
import {
  formatCollectionInquiryRow,
  getCollectionInquiryMailto,
} from '../../Map/utils/collectionInquiryDisplay';
import { LEADS_TEXT_MUTED, LEADS_TEXT_PRIMARY, LEADS_TEXT_SECONDARY } from './leadsUi.styles';

const linkSx = {
  color: LEADS_TEXT_SECONDARY,
  textDecorationColor: 'rgba(255,255,255,0.25)',
  '&:hover': { color: '#FFFFFF' },
};

/**
 * Una consulta del lead: dónde (lista · registro), fecha, mensaje y contacto.
 * Sin etapa propia: la etapa vive en el interés (V3-3).
 * `compact` (adentro de un interés): solo la lista, el registro ya está arriba.
 * Manual (`origin: manual_entry`): sin lista; chips «Manual» + canal y el
 * registro (o “Sin registro”). Fecha = `occurred_at ?? created_at`.
 *
 * `onAssignRecord` (dueño / editor): «Asignar registro» en una manual abierta
 * (sin `lot_id` / `user_lot_id` / `parcel_id`).
 *
 * @param {{ inquiry: object, compact?: boolean, onAssignRecord?: ((inquiry: object) => void)|null }} props
 */
export default function LeadInquiryCard({ inquiry: inq, compact = false, onAssignRecord = null }) {
  const display = formatCollectionInquiryRow(inq);
  const manual = isManualInquiry(inq);
  const originTags = getManualInquiryTags(inq);
  const collectionName = manual ? '' : getInquiryCollectionLabel(inq);
  const itemParts = getLeadItemLabelParts(
    inq.item_label,
    inq.parcel_id ? 'cad_parcel' : inq.user_lot_id ? 'user_lot' : 'cadastral_lot',
  );
  const hasRecord = Boolean(inq.parcel_id || inq.user_lot_id || inq.lot_id);
  const itemText = manual && !hasRecord ? 'Sin registro' : (itemParts.text || display.itemLabel);
  const where = compact
    ? collectionName
    : [collectionName, itemText].filter(Boolean).join(' · ');
  return (
    <Box
      data-testid="lead-inquiry"
      sx={{
        display: 'flex', flexDirection: 'column', gap: 0.5, px: 1.25, py: 1,
        borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.04)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Typography
          title={where}
          sx={{ flex: 1, minWidth: 0, fontSize: '12px', fontWeight: 600, color: LEADS_TEXT_PRIMARY }}
          noWrap
        >
          {!compact && !manual && itemParts.isCadastral ? (
            <>
              {collectionName}
              <Box component="span" sx={{ fontWeight: 400, color: LEADS_TEXT_SECONDARY }}>
                {` · ${itemText}`}
              </Box>
            </>
          ) : where}
        </Typography>
        <InquiryOriginTags tags={originTags} />
        {display.unseen && (
          <Box
            aria-label="Consulta sin ver"
            sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#489291', flexShrink: 0 }}
          />
        )}
        <Typography sx={{ fontSize: '11px', color: LEADS_TEXT_MUTED, whiteSpace: 'nowrap' }}>
          {formatDateTimeShortAR(getInquiryDisplayAt(inq)) || ''}
        </Typography>
      </Box>
      {display.message && (
        <Typography sx={{ fontSize: '13px', color: LEADS_TEXT_PRIMARY, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {display.message}
        </Typography>
      )}
      {(display.visitorName || display.visitorEmail) && (
        <Typography sx={{ fontSize: '11px', color: LEADS_TEXT_SECONDARY }} noWrap>
          {display.visitorName}
          {display.visitorEmail && (
            <>
              {display.visitorName ? ' · ' : ''}
              <Link href={getCollectionInquiryMailto(display.visitorEmail)} sx={linkSx}>
                {display.visitorEmail}
              </Link>
            </>
          )}
        </Typography>
      )}
      {manual && !hasRecord && onAssignRecord && (
        <Button
          size="small"
          onClick={() => onAssignRecord(inq)}
          aria-label="Asignar registro a esta consulta"
          sx={{ ...leadsSummaryClearButtonSx, alignSelf: 'flex-start', ml: -0.5 }}
        >
          Asignar registro
        </Button>
      )}
    </Box>
  );
}
